'use client';
import { Card, Spinner, ProgressBar } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faCalendar,
  faClock,
  faMapPin,
  faPoundSign,
  faUsers,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

interface Shift {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  address?: string;
  pay_rate: number;
  required_locums: number;
  booked_locums_count: number;
  status: string;
  locum_role: string;
  summary?: string;
}

interface PostedShiftsCardProps {
  shifts: Shift[];
  loading: boolean;
}

const PostedShiftsCard = ({ shifts, loading }: PostedShiftsCardProps) => {
  const navigate = useNavigate();

  const upcomingShifts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return shifts
      .filter(shift => {
        const shiftDate = new Date(shift.date);
        shiftDate.setHours(0, 0, 0, 0);
        return shiftDate >= today;
      })
      .sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.start_time}`);
        const dateB = new Date(`${b.date} ${b.start_time}`);
        return dateA.getTime() - dateB.getTime();
      })
      .slice(0, 5);
  }, [shifts]);

  const getStatusColor = (shift: Shift) => {
    if (shift.status === 'closed') return 'secondary';
    if (shift.booked_locums_count >= shift.required_locums) return 'success';
    return 'warning';
  };

  const getStatusLabel = (shift: Shift) => {
    if (shift.status === 'closed') return 'Closed';
    if (shift.booked_locums_count >= shift.required_locums)
      return 'Fully Booked';
    return 'Open';
  };

  if (loading) {
    return (
      <Card className="border border-translucent shadow-sm h-100">
        <Card.Header className="bg-body-emphasis">
          <Card.Title className="mb-0 fs-8 text-body-emphasis">
            <FontAwesomeIcon icon={faCalendar} className="me-2" />
            Posted Shifts
          </Card.Title>
        </Card.Header>
        <Card.Body className="text-center py-6">
          <Spinner animation="border" role="status" className="text-primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border border-translucent shadow-sm h-100">
      <Card.Header className="bg-body-emphasis d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0 fs-8 text-body-emphasis">
          <FontAwesomeIcon icon={faCalendar} className="me-2" />
          Posted Shifts
        </Card.Title>
        <Badge variant="phoenix" bg="primary" className="fs-10">
          {shifts.length} Total
        </Badge>
      </Card.Header>
      <Card.Body className="p-3 p-lg-4">
        <Button
          color="primary"
          size="sm"
          className="w-100 mb-3"
          onClick={() => navigate('/dashboard/pharmacy/create-shift')}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Create New Shift
        </Button>

        {upcomingShifts.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-body-tertiary mb-0">
              <FontAwesomeIcon icon={faCalendar} className="me-2 fs-6" />
              No upcoming shifts posted
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingShifts.map(shift => {
              const fillPercentage =
                (shift.booked_locums_count / shift.required_locums) * 100;

              return (
                <div
                  key={shift.id}
                  className="border border-translucent rounded-3 p-3 bg-body-emphasis hover-shadow-sm transition-all"
                  style={{ cursor: 'pointer' }}
                >
                  {/* 🔹 Top Row */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="mb-0 fw-semibold text-body-emphasis">
                        {new Date(shift.date).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'short'
                        })}
                      </h6>
                      <small className="text-body-tertiary">
                        {shift.locum_role}
                      </small>
                    </div>

                    <Badge variant="phoenix" bg={getStatusColor(shift)}>
                      {getStatusLabel(shift)}
                    </Badge>
                  </div>

                  {/* 🔹 Info Row */}
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <small className="text-body-tertiary">
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      {shift.start_time.slice(0, 5)} -{' '}
                      {shift.end_time.slice(0, 5)}
                    </small>

                    <small className="text-body-tertiary text-truncate">
                      <FontAwesomeIcon icon={faMapPin} className="me-1" />
                      {shift.location}
                    </small>
                  </div>

                  {/* 🔹 Pay + Slots */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="fw-semibold text-primary">
                      <FontAwesomeIcon icon={faPoundSign} className="me-1" />
                      {shift.pay_rate}/hr
                    </div>

                    <small className="text-body-tertiary">
                      <FontAwesomeIcon icon={faUsers} className="me-1" />
                      {shift.booked_locums_count}/{shift.required_locums}
                    </small>
                  </div>

                  {/* 🔹 Progress */}
                  <ProgressBar
                    now={fillPercentage}
                    variant={fillPercentage >= 100 ? 'success' : 'info'}
                    style={{ height: '5px' }}
                    className="mb-2"
                  />

                  {/* 🔹 Action */}
                  <div className="d-flex justify-content-end">
                    <Button
                      color="secondary"
                      className="btn-phoenix-secondary"
                      size="sm"
                      onClick={() =>
                        navigate(
                          `/dashboard/pharmacy/create-shift?id=${shift.id}`
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                      Edit
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default PostedShiftsCard;
